import React, { useState, useEffect } from 'react';
import { classAPI, gamificationAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ProfilePhoto } from '../ProfilePhoto';
import { XPBar } from '../common/XPBar';
import { 
  Coins, Zap, Star, ArrowLeft, TrendingUp, ShoppingBag, Award, 
  History, Target, Calendar, CheckCircle2, XCircle, Clock, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCoins, formatDateTime } from '../../lib/utils';

export default function StudentProfileDialog({ open, onClose, classId, student }) {
  const [profile, setProfile] = useState(null);
  const [achProgress, setAchProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (open && student) {
      loadProfile();
    }
  }, [open, student, classId]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const [response, achRes] = await Promise.all([
        classAPI.getStudentProfile(classId, student.user_id),
        gamificationAPI.getAchievementsProgress(classId, student.user_id).catch(() => ({ data: null }))
      ]);
      setProfile(response.data);
      setAchProgress(achRes.data);
    } catch (error) {
      toast.error('Error al cargar perfil del estudiante');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <ArrowLeft className="w-5 h-5 cursor-pointer hover:text-blue-600" onClick={onClose} />
            Perfil del Estudiante
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : profile ? (
          <div className="space-y-6">
            {/* Header with student info */}
            <div className="flex items-center gap-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
              <ProfilePhoto photoUrl={profile.student.photo_url} name={profile.student.name} size={80} />
              <div className="flex-1">
                <h3 className="text-2xl font-heading font-bold text-slate-900">{profile.student.name}</h3>
                <p className="text-slate-500">{profile.student.email}</p>
                {profile.student.student_id && (
                  <p className="text-sm text-slate-400 mt-1">ID: {profile.student.student_id}</p>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-100 text-amber-700 rounded-xl font-bold text-xl">
                    <Coins className="w-5 h-5" />
                    {formatCoins(profile.stats.balance)}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Balance</p>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-100 text-purple-700 rounded-xl font-bold text-xl">
                    <Zap className="w-5 h-5" />
                    {profile.stats.total_xp}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">XP Total</p>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-100 text-blue-700 rounded-xl font-bold text-xl">
                    <Star className="w-5 h-5" />
                    Nv. {profile.stats.level}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Nivel</p>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="border-slate-200">
                <CardContent className="pt-4 text-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{profile.stats.activities_completed}</p>
                  <p className="text-sm text-slate-500">Actividades</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="pt-4 text-center">
                  <Award className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{profile.stats.achievements_earned}</p>
                  <p className="text-sm text-slate-500">Logros</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="pt-4 text-center">
                  <ShoppingBag className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{profile.stats.total_purchases}</p>
                  <p className="text-sm text-slate-500">Compras</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="pt-4 text-center">
                  <TrendingUp className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{profile.stats.xp_to_next_level}</p>
                  <p className="text-sm text-slate-500">XP para Nv. {profile.stats.level + 1}</p>
                </CardContent>
              </Card>
            </div>

            {/* Tabs for detailed info */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full justify-start">
                <TabsTrigger value="overview">
                  <Target className="w-4 h-4 mr-2" />
                  Habilidades
                </TabsTrigger>
                <TabsTrigger value="activities">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Actividades
                </TabsTrigger>
                <TabsTrigger value="ledger">
                  <History className="w-4 h-4 mr-2" />
                  Transacciones
                </TabsTrigger>
                <TabsTrigger value="achievements">
                  <Award className="w-4 h-4 mr-2" />
                  Logros
                </TabsTrigger>
                <TabsTrigger value="purchases">
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Compras
                </TabsTrigger>
              </TabsList>

              {/* XP by Objective Tab */}
              <TabsContent value="overview" className="mt-4">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">XP por Habilidad</CardTitle>
                    <CardDescription>Distribución de puntos de experiencia</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {profile.xp_by_objective.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">Sin XP registrado aún</p>
                    ) : (
                      <div className="space-y-4">
                        {profile.xp_by_objective.map((item, idx) => (
                          <div key={idx} className="space-y-1.5" data-testid={`profile-skill-${item.objective_id || idx}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                                <span className="font-medium truncate">{item.objective_name}</span>
                                {item.skill_level_name && (
                                  <Badge
                                    variant="outline"
                                    className="text-[10px] h-5 px-1.5 flex-shrink-0"
                                    style={{ borderColor: item.color, color: item.color }}
                                  >
                                    Nv.{item.skill_level} {item.skill_level_name}
                                  </Badge>
                                )}
                              </div>
                              <Badge className="bg-purple-100 text-purple-700 flex-shrink-0">
                                <Zap className="w-3 h-3 mr-1" />
                                {item.xp}{item.max_xp ? ` / ${item.max_xp}` : ''} XP
                              </Badge>
                            </div>
                            {item.progress_pct != null && (
                              <div className="flex items-center gap-2">
                                <div className="flex-1">
                                  <XPBar pct={item.progress_pct} color={item.color} />
                                </div>
                                <span className="text-xs text-slate-500 w-9 text-right">{item.progress_pct}%</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Completed Activities Tab */}
              <TabsContent value="activities" className="mt-4">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Actividades Completadas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {profile.completed_activities.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">Sin actividades completadas</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {profile.completed_activities.map((item) => (
                          <div key={item.completion_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                              <p className="font-medium">{item.activity_title}</p>
                              <p className="text-xs text-slate-400">{formatDateTime(item.completed_at)}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className="bg-amber-50 text-amber-700">
                                +{item.coin_value} coins
                              </Badge>
                              {item.xp_value > 0 && (
                                <Badge className="bg-purple-50 text-purple-700">
                                  +{item.xp_value} XP
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Ledger Tab */}
              <TabsContent value="ledger" className="mt-4">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Historial de Transacciones</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {profile.ledger.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">Sin transacciones</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {profile.ledger.map((entry) => (
                          <div key={entry.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                              <p className="font-medium text-sm">{entry.note || entry.type}</p>
                              <p className="text-xs text-slate-400">{formatDateTime(entry.created_at)}</p>
                            </div>
                            <span className={`font-bold ${entry.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {entry.amount >= 0 ? '+' : ''}{formatCoins(entry.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Achievements Tab */}
              <TabsContent value="achievements" className="mt-4">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Logros</CardTitle>
                    {achProgress && (
                      <CardDescription>
                        {achProgress.earned_count} de {achProgress.total} desbloqueados
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    {achProgress && achProgress.achievements.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {achProgress.achievements.map((ach) => (
                          <div
                            key={ach.achievement_id}
                            className={`flex items-center gap-3 p-3 rounded-lg border ${
                              ach.earned ? 'bg-amber-50/60 border-amber-200' : 'bg-slate-50 border-slate-100'
                            }`}
                            data-testid={`profile-ach-${ach.achievement_id}`}
                          >
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: ach.earned ? ach.badge_color + '20' : '#E2E8F0' }}
                            >
                              {ach.earned ? (
                                <Award className="w-5 h-5" style={{ color: ach.badge_color }} />
                              ) : (
                                <Lock className="w-4 h-4 text-slate-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium truncate ${ach.earned ? 'text-slate-800' : 'text-slate-500'}`}>{ach.name}</p>
                              {ach.earned ? (
                                <p className="text-xs text-emerald-600 flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" />
                                  {ach.earned_at ? formatDateTime(ach.earned_at) : 'Obtenido'}
                                </p>
                              ) : (
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="flex-1">
                                    <XPBar pct={ach.progress_pct} color={ach.badge_color} height="h-1.5" />
                                  </div>
                                  <span className="text-[10px] text-slate-500 whitespace-nowrap">
                                    {ach.progress_pct}% · {ach.current_xp}/{ach.required_xp} XP
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : profile.achievements.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">Sin logros obtenidos aún</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        {profile.achievements.map((ach, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: ach.badge_color + '20' }}
                            >
                              <Award className="w-5 h-5" style={{ color: ach.badge_color }} />
                            </div>
                            <div>
                              <p className="font-medium">{ach.name}</p>
                              <p className="text-xs text-slate-400">{formatDateTime(ach.earned_at)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Purchases Tab */}
              <TabsContent value="purchases" className="mt-4">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Historial de Compras</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {profile.purchases.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">Sin compras realizadas</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {profile.purchases.map((purchase, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <ShoppingBag className="w-5 h-5 text-blue-500" />
                              <div>
                                <p className="font-medium">{purchase.product_name}</p>
                                <p className="text-xs text-slate-400">{formatDateTime(purchase.purchased_at)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className="bg-amber-50 text-amber-700">
                                -{formatCoins(purchase.price_paid)}
                              </Badge>
                              {purchase.status === 'completed' && (
                                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                              )}
                              {purchase.status === 'pending' && (
                                <Clock className="w-4 h-4 text-amber-500" />
                              )}
                              {purchase.status === 'rejected' && (
                                <XCircle className="w-4 h-4 text-red-500" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            <p className="text-xs text-slate-400 text-center">
              Inscrito desde: {formatDateTime(profile.student.enrolled_at)}
            </p>
          </div>
        ) : (
          <p className="text-center text-slate-500 py-8">No se pudo cargar el perfil</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
